// Comprobante de cierre de caja (para imprimir en la ticketera).
import { PAYMENT_METHODS, type CashSummary, type PaymentMethod } from '@pos/core';
import type { CashSessionRow } from '@pos/local-db';
import { escapeHtml } from '../pos/ticket.ts';

const dateFormat = new Intl.DateTimeFormat('es-AR', { dateStyle: 'short', timeStyle: 'short' });

export interface CashReportData {
  businessName: string;
  /** Caja (y dispositivo, si es una caja de un solo dispositivo). */
  placeName: string;
  session: CashSessionRow;
  /** expected = null: arqueo ciego (no se imprime el esperado). */
  summary: Omit<CashSummary, 'expected'> & { expected: number | null };
  openedBy: string;
  closedBy: string | null;
  money: (cents: number) => string;
}

const methodLabel = (m: string) => PAYMENT_METHODS[m as PaymentMethod]?.label ?? m;

export function cashReportHtml(d: CashReportData, width: 58 | 80): string {
  const { session: s, summary: sum, money } = d;
  const row = (left: string, right: string, strong = false) =>
    `<div class="t-row${strong ? ' t-strong' : ''}"><span>${escapeHtml(left)}</span><span>${escapeHtml(right)}</span></div>`;
  const parts: string[] = [];
  parts.push(`<div class="t-center t-strong t-big">${escapeHtml(d.businessName)}</div>`);
  parts.push(`<div class="t-center t-strong">${s.closed_at ? 'CIERRE DE CAJA' : 'ESTADO DE CAJA'}</div>`);
  parts.push(`<div class="t-center">${escapeHtml(d.placeName)}</div>`);
  parts.push('<hr>');
  parts.push(row('Apertura', dateFormat.format(new Date(s.opened_at))), row('Abrió', d.openedBy));
  if (s.closed_at) parts.push(row('Cierre', dateFormat.format(new Date(s.closed_at))), row('Cerró', d.closedBy ?? '—'));
  parts.push('<hr>');
  parts.push(row(`Ventas (${sum.salesCount})`, money(sum.salesTotal)));
  for (const [m, v] of Object.entries(sum.byMethod)) parts.push(row(`  ${methodLabel(m)}`, money(v)));
  if (sum.returnsCount) {
    parts.push(row(`Devoluciones (${sum.returnsCount})`, `−${money(sum.returnsTotal)}`));
    for (const [m, v] of Object.entries(sum.refundsByMethod)) parts.push(row(`  ${methodLabel(m)}`, `−${money(v)}`));
  }
  parts.push('<hr>');
  parts.push(row('Fondo inicial', money(sum.opening)));
  parts.push(row('+ Ventas en efectivo', money(sum.cashSales)));
  if (sum.cashRefunds) parts.push(row('− Devoluciones en efectivo', money(sum.cashRefunds)));
  if (sum.accountPayments) parts.push(row('+ Cobros de cuenta corriente', money(sum.accountPayments)));
  if (sum.ins) parts.push(row('+ Ingresos', money(sum.ins)));
  if (sum.outs) parts.push(row('− Retiros', money(sum.outs)));
  if (sum.expenses) parts.push(row('− Gastos', money(sum.expenses)));
  const expected = s.expected_cash ?? sum.expected;
  if (expected != null) parts.push(row('Efectivo esperado', money(expected), true));
  if (s.counted_cash != null && expected != null) {
    const diff = s.difference ?? s.counted_cash - expected;
    parts.push(row('Efectivo contado', money(s.counted_cash), true));
    parts.push(row('Diferencia', `${diff > 0 ? '+' : ''}${money(diff)}`, true));
  }
  if (s.close_notes) parts.push(`<div>Notas: ${escapeHtml(s.close_notes)}</div>`);
  return `<div class="t-ticket" style="width:${width === 58 ? '48mm' : '72mm'}">${parts.join('')}</div>`;
}
